import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  LifecycleManagementAllEvents,
  LifecycleManagementEventStatusType,
  LifecycleManagementEventStrategyType,
  LifecycleManagementUpsertEventRequestDto,
} from "@lib/ShiOneClient";
import { getLifecycleManagementStatusColor } from "shared-ui/src/components/lifecycle-management/getLifecycleManagementStatusColor";
import {
  getLifecycleStatusLabel,
  getLifecycleStrategyLabel,
  normalizeLifecycleStatus,
  normalizeLifecycleStrategy,
  compareLifecycleStatusDisplay,
} from "shared-ui/src/components/lifecycle-management/lifecycleManagementEnums";
import { LifecycleEventTypeTooltip } from "shared-ui/src/components/lifecycle-management/LifecycleEventTypeTooltip";
import LifecycleUpdateEventDateDialog from "shared-ui/src/components/lifecycle-management/LifecycleUpdateEventDateDialog";
import useUpdateAssetLifecycleEventData from "./hooks/useUpdateAssetLifecycleEventsData";
import { Toast } from "shared-ui/src/components/Toast";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon";
import { CalendarToday, InfoOutlined } from "@mui/icons-material";
import { DateTime } from "luxon";
import { useClaims } from "../../../../../../auth/ClaimsProvider";
import { PeopleMultiSelect } from "shared-ui/src/components/lifecycle-management/PeopleMultiSelect";
import { DrawerReadOnlyField } from "shared-ui/src/components/lifecycle-management/DrawerReadOnlyField";
import { LifecycleManagementAccountExecutiveField } from "shared-ui/src/components/lifecycle-management/LifecycleManagementAccountExecutiveField";
import {
  mapAssignedToToPeopleOption,
  partitionCustomerOwnerOptions,
} from "shared-ui/src/components/lifecycle-management/peopleOptionMapper";
import CxEmployeeAvatar, { AvatarSize } from "@components/CxEmployeeAvatar";
import useGetAssetLifecycleManagementCustomerOwners from "./hooks/useGetAssetLifecycleManagementCustomerOwners";
import {
  arePeopleSelectionsEqual,
  normalizePeopleSelection,
} from "shared-ui/src/components/lifecycle-management/peopleSelection";

const formatStatusLabel = (status: string): string =>
  getLifecycleStatusLabel(status);

const allStatuses = Object.values(LifecycleManagementEventStatusType).sort(
  compareLifecycleStatusDisplay,
);

const normalizeStrategyValue = (
  stringValue?: string | null,
): LifecycleManagementEventStrategyType =>
  normalizeLifecycleStrategy<LifecycleManagementEventStrategyType>(stringValue);

const normalizeStatusValue = (
  stringValue?: string | null,
): LifecycleManagementEventStatusType | "" =>
  normalizeLifecycleStatus<LifecycleManagementEventStatusType>(stringValue) ??
  "";

const PleaseSelectAStrategy = "Please select a strategy.";

const AssetLifecycleManagementDrawerEvents = ({
  selectedLifecycleEventData,
  setSelectedLifecycleEventData,
  onSaveHandlerChange,
  onSaveStateChange,
  onSaveSuccess,
  hasAssetOrderDataManagePermission,
}: {
  selectedLifecycleEventData: LifecycleManagementAllEvents | undefined;
  setSelectedLifecycleEventData?: Dispatch<
    SetStateAction<LifecycleManagementAllEvents | undefined>
  >;
  onSaveHandlerChange?: (handler: (() => void) | undefined) => void;
  onSaveStateChange?: (disable: boolean, saving: boolean) => void;
  onSaveSuccess?: () => void;
  hasAssetOrderDataManagePermission?: boolean;
  handleClose?: () => void;
}) => {
  const theme = useTheme();

  const isBillingEventType =
    selectedLifecycleEventData?.eventType === "Billing";
  const { isShi, getClaimValue } = useClaims();
  const isImpersonating =
    getClaimValue("isImpersonating")?.toLowerCase() === "true";
  const canUseRequestedStatus = isImpersonating || isShi();

  const [eventDate, setEventDate] = useState<DateTime | undefined>(
    // Need an initial time to prevent uncontrolled to controlled input warning
    DateTime.now(),
  );
  const [customerFacingNotes, setCustomerFacingNotes] = useState<string>("");
  const [strategy, setStrategy] = useState<
    LifecycleManagementEventStrategyType | ""
  >("");
  const [status, setStatus] = useState<LifecycleManagementEventStatusType | "">(
    "",
  );
  const currentStatusIsRequested =
    status === LifecycleManagementEventStatusType.Requested ||
    normalizeStatusValue(selectedLifecycleEventData?.status) ===
      LifecycleManagementEventStatusType.Requested;
  const availableStatuses = useMemo(
    () =>
      allStatuses.filter(
        (statusOption) =>
          canUseRequestedStatus ||
          currentStatusIsRequested ||
          statusOption !== LifecycleManagementEventStatusType.Requested,
      ),
    [canUseRequestedStatus, currentStatusIsRequested],
  );
  const [strategyError, setStrategyError] = useState<string | undefined>();
  const [dateInputError, setDateInputError] = useState(false);
  const [dateConfirmOpen, setDateConfirmOpen] = useState(false);
  const [customerOwner, setCustomerOwner] = useState<string[]>([]);
  type InitialValues = {
    eventDate: string | undefined;
    // Displayed status (manual, else derived); only used to detect a user change.
    status: string;
    // Status the user explicitly stored, "" when none; the only status ever sent as initialValue.
    manualStatus: string;
    strategy: LifecycleManagementEventStrategyType | "";
    customerFacingNotes: string;
    customerOwner: string[];
  };

  const [initialValues, setInitialValues] = useState<InitialValues>({
    eventDate: undefined,
    status: "",
    manualStatus: "",
    strategy: "",
    customerFacingNotes: "",
    customerOwner: [],
  });

  const { data: customerOwnersData, isLoading: isCustomerOwnersLoading } =
    useGetAssetLifecycleManagementCustomerOwners(
      selectedLifecycleEventData?.crmAccountId?.trim(),
    );

  const assignedToOptions = useMemo(
    () =>
      (selectedLifecycleEventData?.assignedTo ?? [])
        .map(mapAssignedToToPeopleOption)
        .filter((option) => option.id !== "" && option.displayName !== ""),
    [selectedLifecycleEventData?.assignedTo],
  );
  const assignedToIds = useMemo(
    () =>
      normalizePeopleSelection(
        (selectedLifecycleEventData?.assignedTo ?? []).map(
          (assignedTo) => assignedTo.entraId,
        ),
      ),
    [selectedLifecycleEventData?.assignedTo],
  );

  const { options: customerOwnerOptions, readOnlyOptions: customerOwnerReadOnlyOptions } =
    useMemo(
      () => partitionCustomerOwnerOptions(customerOwnersData ?? []),
      [customerOwnersData],
    );

  const { mutateAsync, isPending, reset } = useUpdateAssetLifecycleEventData();
  const isLifecycleFieldEditingDisabled =
    !hasAssetOrderDataManagePermission || isPending || isBillingEventType;
  const isPeopleSelectionEditingDisabled =
    !hasAssetOrderDataManagePermission || isPending;

  const formattedEventDate = useMemo(
    () => eventDate?.setZone("utc").toFormat("MM/dd/yyyy"),
    [eventDate],
  );

  const customerOwnerChanged = useMemo(
    () => !arePeopleSelectionsEqual(initialValues.customerOwner, customerOwner),
    [customerOwner, initialValues.customerOwner],
  );

  const statusChanged = status !== initialValues.status;

  // Quote Needed needs a strategy when the user picks it or it is their stored status, not when it is only
  // derived from the Renewals App, so notes and customer-owner edits on those events still save.
  const requestedStatusNeedsStrategy =
    canUseRequestedStatus &&
    status === LifecycleManagementEventStatusType.Requested &&
    (statusChanged ||
      initialValues.manualStatus === LifecycleManagementEventStatusType.Requested);

  const hasFormChanges = useMemo(() => {
    return (
      formattedEventDate !== initialValues.eventDate ||
      statusChanged ||
      strategy !== initialValues.strategy ||
      customerFacingNotes !== initialValues.customerFacingNotes ||
      customerOwnerChanged
    );
  }, [
    formattedEventDate,
    statusChanged,
    strategy,
    customerFacingNotes,
    customerOwnerChanged,
    initialValues.eventDate,
    initialValues.strategy,
    initialValues.customerFacingNotes,
  ]);

  useEffect(() => {
    if (!selectedLifecycleEventData) {
      return;
    }

    const normalizedStatus = normalizeStatusValue(
      selectedLifecycleEventData.status,
    );
    const normalizedStrategy = normalizeStrategyValue(
      selectedLifecycleEventData.strategy,
    );
    const initialCustomerOwner = normalizePeopleSelection(
      selectedLifecycleEventData.customerOwner ?? [],
    );
    const parsedOverrideDate = selectedLifecycleEventData?.eventDate
      ? DateTime.fromFormat(
          selectedLifecycleEventData.eventDate,
          "MM/dd/yyyy",
          { zone: "utc" },
        )
      : undefined;

    setStrategy(normalizedStrategy);
    setStatus(normalizedStatus);
    setCustomerFacingNotes(
      selectedLifecycleEventData.customerFacingNotes ?? "",
    );
    setEventDate(parsedOverrideDate);
    setCustomerOwner(initialCustomerOwner);
    setInitialValues({
      eventDate: selectedLifecycleEventData?.eventDate,
      status: normalizedStatus,
      manualStatus: normalizeStatusValue(selectedLifecycleEventData.manualStatus),
      strategy: normalizedStrategy,
      customerFacingNotes: selectedLifecycleEventData.customerFacingNotes ?? "",
      customerOwner: initialCustomerOwner,
    });
  }, [selectedLifecycleEventData]);

  useEffect(() => {
    if (!onSaveStateChange) {
      return;
    }

    onSaveStateChange(
      isPending || !hasFormChanges || dateInputError,
      isPending,
    );
  }, [hasFormChanges, isPending, onSaveStateChange, dateInputError]);

  const performSave = useCallback(() => {
    if (!selectedLifecycleEventData?.id) return;

    if (!hasFormChanges || isPending || dateInputError) {
      return;
    }

    if (
      requestedStatusNeedsStrategy &&
      (!strategy ||
        strategy === LifecycleManagementEventStrategyType.NotSelected)
    ) {
      setStrategyError(PleaseSelectAStrategy);
      return;
    }

    setStrategyError(undefined);

    const shouldUpdateManualOverride =
      formattedEventDate !== initialValues.eventDate
        ? formattedEventDate
        : undefined;

    const shouldClearManualOverride =
      !!initialValues?.eventDate && !formattedEventDate;

    mutateAsync({
      manualOverrideEventDate: shouldUpdateManualOverride,
      clearManualOverrideEventDate: shouldClearManualOverride,
      uniqueId: selectedLifecycleEventData?.id,
      status: statusChanged ? status : undefined,
      customerFacingNotes:
        customerFacingNotes !== initialValues.customerFacingNotes
          ? customerFacingNotes
          : undefined,
      strategy:
        strategy !== initialValues.strategy && strategy ? strategy : undefined,
      customerOwner: customerOwnerChanged ? customerOwner : undefined,
      // The repository persists initialValue.status when no status is sent, so it must be the stored manual status.
      initialValue: {
        uniqueIdentifier: selectedLifecycleEventData.id,
        status: initialValues.manualStatus || undefined,
        strategy: initialValues.strategy,
        customerFacingNotes: initialValues.customerFacingNotes,
        manualOverrideEventDate: initialValues.eventDate,
        customerOwner: initialValues.customerOwner,
      } as LifecycleManagementUpsertEventRequestDto,
    })
      .then(() => {
        const toastMessage =
          statusChanged &&
          status === LifecycleManagementEventStatusType.Requested
            ? "Renewal/Refresh created successfully!"
            : "Saved successfully!";

        Toast(toastMessage);
        setSelectedLifecycleEventData?.((prev) => {
          if (!prev) {
            return undefined;
          }

          return new LifecycleManagementAllEvents({
            ...prev,
            eventDate: formattedEventDate || undefined,
            status: status,
            manualStatus: statusChanged ? status : prev.manualStatus,
            strategy:
              strategy || LifecycleManagementEventStrategyType.NotSelected,
            customerFacingNotes,
            customerOwner: customerOwnerChanged
              ? customerOwner
              : prev.customerOwner,
          });
        });
        setInitialValues((prev) => ({
          ...prev,
          eventDate: formattedEventDate ?? undefined,
          status,
          manualStatus: statusChanged ? status : prev.manualStatus,
          strategy,
          customerFacingNotes,
          customerOwner: customerOwnerChanged
            ? customerOwner
            : prev.customerOwner,
        }));
        reset();
        onSaveSuccess?.();
      })
      .catch(() => {
        Toast("Failed to save. Please try again.", true);
        reset();
      });
  }, [
    canUseRequestedStatus,
    requestedStatusNeedsStrategy,
    formattedEventDate,
    hasFormChanges,
    isPending,
    customerFacingNotes,
    selectedLifecycleEventData?.id,
    status,
    statusChanged,
    strategy,
    initialValues.eventDate,
    initialValues.customerFacingNotes,
    initialValues.status,
    initialValues.manualStatus,
    initialValues.strategy,
    initialValues.customerOwner,
    mutateAsync,
    dateInputError,
    customerOwner,
    customerOwnerChanged,
    onSaveSuccess,
  ]);

  const eventDateChanged = formattedEventDate !== initialValues.eventDate;

  const handleSave = useCallback(() => {
    if (!selectedLifecycleEventData?.id) return;

    if (!hasFormChanges || isPending || dateInputError) {
      return;
    }

    if (eventDateChanged) {
      setDateConfirmOpen(true);
      return;
    }

    performSave();
  }, [
    eventDateChanged,
    hasFormChanges,
    isPending,
    dateInputError,
    performSave,
    selectedLifecycleEventData?.id,
  ]);

  const handleConfirmDateUpdate = useCallback(() => {
    setDateConfirmOpen(false);
    performSave();
  }, [performSave]);

  useEffect(() => {
    if (!onSaveHandlerChange) {
      return;
    }

    onSaveHandlerChange(handleSave);
    return () => onSaveHandlerChange(undefined);
  }, [handleSave, onSaveHandlerChange]);

  useEffect(() => {
    if (requestedStatusNeedsStrategy) {
      setStrategyError(
        !strategy ||
          strategy === LifecycleManagementEventStrategyType.NotSelected
          ? PleaseSelectAStrategy
          : undefined,
      );
    } else {
      setStrategyError(undefined);
    }
  }, [requestedStatusNeedsStrategy, strategy]);

  return (
    <>
      <Box
        sx={{
          height: "100%",
          backgroundColor: "table.header",
          p: 3,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <Box>
            <InputLabel sx={{ mb: 1, fontSize: 12 }}>Event Type</InputLabel>
            <LifecycleEventTypeTooltip
              eventType={selectedLifecycleEventData?.eventType ?? ""}
            />
          </Box>
        </Box>
        <Box sx={{ mb: 2 }}>
          <LocalizationProvider
            dateAdapter={AdapterLuxon}
            adapterLocale="en-US"
          >
            <DatePicker
              sx={{
                background: theme.palette.background.paper,
                width: "100%",
              }}
              slots={{
                openPickerIcon: CalendarToday,
              }}
              slotProps={{
                actionBar: {
                  actions: ["clear", "accept"],
                },
                inputAdornment: {
                  position: "start",
                },
                textField: {
                  error: dateInputError,
                },
              }}
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  Event Date
                  <Tooltip
                    title="Updating the event date will automatically update the event date on all line items."
                    placement="top"
                  >
                    <Box
                      component="span"
                      tabIndex={0}
                      role="button"
                      aria-label="Event date update information"
                      sx={{
                        display: "inline-flex",
                        cursor: "pointer",
                      }}
                    >
                      <InfoOutlined
                        sx={{
                          fontSize: "1rem",
                          color: "action.active",
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
              }
              value={eventDate ?? null}
              onError={(error) => setDateInputError(error === "invalidDate")}
              onChange={(date) => {
                setEventDate(
                  date
                    ? date.setZone("utc", { keepLocalTime: true })
                    : undefined,
                );
              }}
              orientation={"landscape"}
              disabled={isLifecycleFieldEditingDisabled}
              timezone="UTC"
            />
          </LocalizationProvider>
          {selectedLifecycleEventData?.ruleBasedEventDate && (
            <Box>
              <Typography variant="caption" color="textSecondary">
                System Event Date:{" "}
                {selectedLifecycleEventData?.ruleBasedEventDate}.
                {selectedLifecycleEventData?.eventType === "Renewal" &&
                  " Maintenance End Date of Items Purchased"}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ my: 2, pb: 2 }}>
          <Alert severity="info">
            <AlertTitle
              aria-label="Compelling Event Date Description"
              sx={{ fontSize: "14px", fontWeight: "400" }}
            >
              Changing the event date for a renewal or refresh in this system
              does not modify the official contract dates provided by the OEM.
              Always refer to the OEM-verified contract details for accurate
              timelines.
            </AlertTitle>
          </Alert>
        </Box>

        <Box sx={{ display: "flex", mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              labelId="status-select-label"
              disabled={isLifecycleFieldEditingDisabled}
              id="status-select"
              value={status}
              label="Status"
              onChange={(e) =>
                setStatus(e.target.value as LifecycleManagementEventStatusType)
              }
              sx={{
                width: "100%",
                backgroundColor: theme.palette.background.paper,
              }}
              size="small"
            >
              {availableStatuses.map(
                (statusOption: LifecycleManagementEventStatusType) => (
                  <MenuItem key={statusOption} value={statusOption}>
                    <Chip
                      label={formatStatusLabel(statusOption)}
                      variant="outlined"
                      size={"small"}
                      sx={{
                        borderColor:
                          getLifecycleManagementStatusColor(statusOption),
                      }}
                    />
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: "flex", mb: 2 }}>
          <FormControl fullWidth error={!!strategyError}>
            <InputLabel>Strategy</InputLabel>
            <Select
              labelId="strategy-select-label"
              id="strategy-select"
              disabled={isLifecycleFieldEditingDisabled}
              value={strategy}
              label="Strategy"
              onChange={(e) =>
                setStrategy(
                  e.target.value as LifecycleManagementEventStrategyType | "",
                )
              }
              sx={{
                width: "100%",
                backgroundColor: theme.palette.background.paper,
              }}
              size="small"
            >
              {Object.values(LifecycleManagementEventStrategyType).map(
                (strategyType: LifecycleManagementEventStrategyType) => (
                  <MenuItem key={strategyType} value={strategyType}>
                    {getLifecycleStrategyLabel(strategyType)}
                  </MenuItem>
                ),
              )}
            </Select>
            <FormHelperText>{strategyError}</FormHelperText>
          </FormControl>
        </Box>
        <Box sx={{ my: 3 }}>
          <PeopleMultiSelect
            label="Assigned To"
            options={assignedToOptions}
            selectedIds={assignedToIds}
            renderAvatar={(person) => (
              <CxEmployeeAvatar
                entraId={person.id}
                displayName={person.displayName}
                photoSize={AvatarSize.small}
              />
            )}
            readOnly
          />
        </Box>
        <Box sx={{ my: 3 }}>
          <Typography variant="caption" color="textSecondary">
            Account Executive
          </Typography>
          <LifecycleManagementAccountExecutiveField
            accountExecutive={selectedLifecycleEventData?.accountExecutive}
          />
        </Box>
        <Box
          sx={{
            my: 3,
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background.paper,
            },
          }}
        >
          <PeopleMultiSelect
            label="Customer Owner"
            options={customerOwnerOptions}
            fallbackOptions={customerOwnerReadOnlyOptions}
            selectedIds={customerOwner}
            onChange={setCustomerOwner}
            loading={isCustomerOwnersLoading}
            disabled={isPeopleSelectionEditingDisabled}
          />
        </Box>
        <Box sx={{ my: 3 }}>
          <DrawerReadOnlyField
            label="Prev. Delivery Contact"
            value={selectedLifecycleEventData?.prevDeliveryContact}
          />
        </Box>
        <Box>
          <TextField
            id={`lifecycle-event-notes-${selectedLifecycleEventData?.id ?? "new"}`}
            label="Notes"
            disabled={!hasAssetOrderDataManagePermission || isPending}
            fullWidth
            multiline
            rows={8}
            value={customerFacingNotes}
            onChange={(e) => setCustomerFacingNotes(e.target.value)}
            placeholder="Add notes to this event"
            sx={{ backgroundColor: theme.palette.background.paper }}
          />
        </Box>
      </Box>
      <LifecycleUpdateEventDateDialog
        dateConfirmOpen={dateConfirmOpen}
        closeDialogAction={() => setDateConfirmOpen(false)}
        pendingDate={formattedEventDate}
        updateDateAction={handleConfirmDateUpdate}
      />
    </>
  );
};

export default AssetLifecycleManagementDrawerEvents;
